import api from '@/services/api';
import type { AgentType, Substage } from '@/modules/job/store/useJobWizard';

const BASE = '/v2/application';

export interface ApplicationCandidate {
    name: string;
    email?: string;
    phone?: string;
    avatarUrl?: string;
}

export interface ApplicationTimelineEntry {
    _id: string;
    fromStageId?: string;
    toStageId?: string;
    fromStageName?: string;
    toStageName?: string;
    action: 'applied' | 'moved' | 'rejected' | 'withdrawn' | 'note';
    performedBy?: string;
    performedAt: string;
    notes?: string;
}

export interface ApplicationDocument {
    type: 'resume' | 'cover_letter' | 'portfolio' | 'other';
    url: string;
    filename: string;
    uploadedAt: string;
}

// Mirrors the server-side `sourcedProfileSchema` on Application. Populated
// for `source === 'sourcing'` rows. Currently filled by seed scripts; a
// future LinkedIn / sourcing-agent integration will populate the same shape.
export interface SourcedExperienceEntry {
    company: string;
    title: string;
    startDate?: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
}

export interface SourcedEducationEntry {
    school: string;
    degree?: string;
    field?: string;
}

export interface SourcedProfile {
    headline?: string;
    summary?: string;
    location?: string;
    yearsExperience?: number | null;
    matchScore?: number | null;
    linkedinUrl?: string;
    skills?: string[];
    experience?: SourcedExperienceEntry[];
    education?: SourcedEducationEntry[];
    provider?: string;
    providerId?: string;
}

export interface CvScreeningSnapshot {
    resultId: string;
    screenerId: string;
    stageId: string;
    verdict: string | null;
    criteriaMet: number | null;
    criteriaTotal: number | null;
    profileStrengthScore: number | null;
    profileStrengthMax: number | null;
    completedAt: string | null;
}

export interface Application {
    _id: string;
    organizationId: string;
    workspaceId: string;
    jobPostingId: string;
    pipelineId: string;
    currentStageId: string;
    currentStageName: string;
    currentSubstage: string | null;
    personId: string | null;
    overallStatus: 'active' | 'rejected' | 'hired';
    jobTitle: string | null;
    candidate: ApplicationCandidate;
    source: string;
    sourceRef?: {
        idibuApplicantId?: string;
        idibuPortalId?: number;
        idibuPortalName?: string;
    } | null;
    appliedAt: string;
    stageEnteredAt: string;
    customFields: Record<string, unknown>;
    documents: ApplicationDocument[];
    /**
     * Populated for `source === 'sourcing'` applications. The recruiter UI
     * renders a LinkedIn-style profile card from this when present.
     */
    sourcedProfile?: SourcedProfile | null;
    timeline: ApplicationTimelineEntry[];
    activeCvScreening: CvScreeningSnapshot | null;
    cvScreeningHistory: CvScreeningSnapshot[];
    /**
     * Task summary, populated by the kanban list endpoint (Phase F).
     * Empty/zero when the application has no tasks. `primaryPendingTaskId`
     * points at the highest-priority pending task — used by the kanban click
     * handler to thread taskId through to the FullPager overlay.
     */
    tasks?: {
        pending: number;
        inProgress: number;
        completed: number;
        primaryPendingTaskId: string | null;
        primaryPendingTaskKind?: 'cv_review' | 'ai_interview_review' | null;
        primaryPendingTaskScope?: { reportKinds: string[] } | null;
    };
    createdAt: string;
    updatedAt: string;
}

export interface KanbanColumnSubstage {
    key: string;
    label: string;
    isFinal?: boolean;
    isRejection?: boolean;
}

export interface KanbanColumn {
    id: string;
    title: string;
    apiStatusValue: string;
    order: number;
    color: string;
    sortOptions?: Array<{ id: string; label: string }>;
    filterGroups?: Array<{ id: string; label: string; ui: string; options?: Array<{ label: string; value: string | number | null }> }>;
    substages?: KanbanColumnSubstage[];
    stageKind?: 'applied' | 'default' | 'agentic';
    agentType?: AgentType | null;
    agentConfigId?: string | null;
    agentConfigStatus?: 'unconfigured' | 'draft' | 'active' | null;
    isSystem?: boolean;
}

export interface KanbanConfigResponse {
    columns: KanbanColumn[];
    search: { placeholder: string; caseSensitive: boolean };
    stageCounts: Record<string, number>;
}

export interface KanbanStageResult {
    data: Application[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
}

export const applicationApi = {
    getKanbanConfig: (jobPostingId: string) =>
        api.get<KanbanConfigResponse>(`${BASE}/kanban/${jobPostingId}/config`),

    getKanbanStage: (jobPostingId: string, stageId: string, params?: {
        searchTerm?: string;
        page?: number;
        limit?: number;
        sortField?: string;
        sortDirection?: string;
        filters?: string;
    }) => api.get<KanbanStageResult>(`${BASE}/kanban/${jobPostingId}/stage/${stageId}`, { params }),

    createApplication: (data: {
        jobPostingId: string;
        candidate: ApplicationCandidate;
        source?: string;
        customFields?: Record<string, unknown>;
        documents?: Array<{ type: string; url: string; filename: string }>;
    }) => api.post<Application>(BASE, data),

    getApplication: (id: string) =>
        api.get<Application>(`${BASE}/${id}`),

    moveApplication: (id: string, targetStageId: string) =>
        api.patch<Application>(`${BASE}/${id}/move`, { targetStageId }),

    updateSubstage: (id: string, substage: string) =>
        api.patch<Application>(`${BASE}/${id}/substage`, { substage }),

    rejectApplication: (id: string, notes?: string) =>
        api.patch<Application>(`${BASE}/${id}/reject`, { notes }),

    bulkMove: (applicationIds: string[], targetStageId: string) =>
        api.post(`${BASE}/bulk-move`, { applicationIds, targetStageId }),

    navigateInContext: (params: {
        jobPostingId: string;
        stageId: string;
        substage?: string;
        targetIndex: number;
        searchTerm?: string;
        sortField?: string;
        sortDirection?: string;
        filters?: Record<string, unknown>;
    }) => api.post<{
        applicationId: string | null;
        candidateName: string | null;
        indexInContext: number;
        totalInContext: number;
    }>(`${BASE}/navigate`, params),
};
